import { Grid, useEventCallback, SelectChangeEvent, IconButton, Typography } from '@mui/material';
import { memo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import FlagValidationFiscalCode from 'shell/components/shared/flag-validation-fiscal-code/flag';
import ReasonMutationList, { ReasonMutationVM } from './reason-mutation-list';
import { FormikErrors, FormikTouched } from 'formik';
import { PmiInputFile } from 'shell/components/shared/file-input';
import {
  AccordionEditDataControlPerson,
  FileExtended,
} from 'shell/view-models/edit-data-control-person';

interface FormEditControlDataProps {
  disabled?: boolean;
  values?: AccordionEditDataControlPerson;
  isEditMode?: boolean;
  onChange: (
    key: keyof AccordionEditDataControlPerson,
    value: AccordionEditDataControlPerson[keyof AccordionEditDataControlPerson]
  ) => void;
  touched?: FormikTouched<AccordionEditDataControlPerson>;
  errors?: FormikErrors<AccordionEditDataControlPerson>;
  fieldsDisabled?: { [K in keyof AccordionEditDataControlPerson]: boolean };
}

const FormEditControlDataComponent = ({
  disabled,
  values,
  onChange,
  isEditMode,
  errors,
  touched,
  fieldsDisabled,
}: FormEditControlDataProps): React.ReactElement => {
  const [fileUploadError, setFileUploadError] = useState('');
  const handleFlagChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('flag', event.target.value);
  });

  const handleMutationChange = useEventCallback((value: string, item?: ReasonMutationVM) => {
    onChange('mutationReason', item);
  });

  const handleAddFile = useEventCallback((files: Array<File>) => {
    setFileUploadError('');
    const [file] = files;
    const reader = new FileReader();
    const maxSize = Math.pow(10, 7);

    if (file.size > maxSize) {
      // eslint-disable-next-line no-console
      console.log('file più grande');
      setFileUploadError('File più grande di 10MB');
      return;
    }

    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};

      const newFiles: Array<FileExtended> = values?.files?.slice() || [];
      const filesNew: Array<FileExtended> = [
        {
          ...file,
          type: file.type,
          name: file.name,
          size: file.size,
          raw: result as string,
        },
      ];

      onChange('files', newFiles.concat(filesNew));
    });

    reader.readAsDataURL(file);
  });

  const onTrashPress = useEventCallback((indexCurr) => {
    let newFiles: Array<FileExtended> = values?.files?.slice() || [];

    newFiles = newFiles.filter((_, index) => index !== indexCurr) || [];

    onChange('files', newFiles);
  });

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data decorrenza"
          value={values?.dateValidity || null}
          disabled={isEditMode ? fieldsDisabled?.dateValidity : disabled}
          onChange={(dataValue) => {
            onChange('dateValidity', dataValue || null);
          }}
          renderInput={(params) => (
            <PmiTextField {...params} id="dateValidity" fullWidth error={false} helperText="" />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FlagValidationFiscalCode
          disabled={isEditMode ? fieldsDisabled?.flag : disabled}
          value={values?.flag || ''}
          onChange={handleFlagChange}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ReasonMutationList
          disabled={isEditMode ? fieldsDisabled?.mutationReason : disabled}
          value={(values?.mutationReason?.id as string) || ''}
          onChange={handleMutationChange}
          error={touched?.mutationReason && !!errors?.mutationReason}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        Carica documento&nbsp;
        <PmiInputFile
          files={values?.files}
          onChange={handleAddFile}
          color="cdmBrandThird"
          multiple
          hideList
          disabled={isEditMode ? fieldsDisabled?.files : disabled}
        />
        {fileUploadError ? (
          <Typography color="cdmBrand.main">{fileUploadError}</Typography>
        ) : undefined}
      </Grid>
      <Grid item xs={12}>
        {values?.files?.map((file, index) => (
          <Grid
            container
            spacing={0}
            columnSpacing={{ xs: 0, md: 1 }}
            item
            xs={12}
            key={`file-${file.name}-${index}`}
            display="flex"
            alignItems="center"
          >
            <Grid item xs={4}>
              <a download={file.name} href={file.raw}>
                {file.name}
              </a>
            </Grid>
            <Grid item xs={5}>
              <IconButton
                color="error"
                disabled={isEditMode ? false : disabled}
                aria-label="eliminare documento allegato"
                component="span"
                onClick={() => onTrashPress(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

FormEditControlDataComponent.displayName = 'Form Edit Control data component';

const FormEditControlData = memo(FormEditControlDataComponent);

export default FormEditControlData;
